import { useEffect, useRef, useState } from "react";
import { Button, Header6 } from "../../../../components/ui";
import { TextInput, PasswordInput } from "../../../../components/ui/inputs";
import { saveElementAsImage } from "../../../../utils/saveImage";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { copyToClipboard } from "../../../../utils/clipboard";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { PhoneNumberInput } from "../../../../components/ui/inputs/PhoneNumberInput";
import { authService } from "../../services/auth.service";
import { VerificationChannel, VerificationChannelsArray } from "../../constants/verificationChannel";
import localization from "../../../../config/localization";
import { Validations } from "../../../../models/constants/validations";
import userDeviceStore from "../../../../store/device.store";
import { AccessMode } from "../../../../models/constants/accessMode";
import { toastService } from "../../../../components/ui/toast/toast.service";

interface OneClickSignupConfirmationProps {
  userName?: string;
  password?: string;
  redirectUrl?: string;
}

export function OneClickSignupConfirmation({
  userName,
  password,
  redirectUrl = "/",
}: OneClickSignupConfirmationProps) {
  const captureRef = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = useState(false);

  const [verificationChannel, setVerificationChannel] = useState<string>(VerificationChannel.Email.value);
  const [notificationData, setNotificationData] = useState<string>("");
  const [isNotificationSendOnEmail, setIsNotificationSendOnEmail] = useState(true);
  const [isNotificationSendOnPhone, setIsNotificationSendOnPhone] = useState(false);
  const [notificationDataError, setNotificationDataError] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => {
      navigate(redirectUrl);
      window.location.reload();
    }, 10000);
  }, []);

  const handleSaveImage = async () => {
    if (!captureRef.current) return;
    await saveElementAsImage(captureRef.current, "sky365-account.png");
  };

  const handleCopy = async () => {
    let accessMode = userDeviceStore.getState().accessModeSource.sourceType ?? AccessMode.Web;
    await copyToClipboard(accessMode, userName, {
      onSuccess: () => {
        setCopied(true);
        toastService.success(localization.username_copied);

        setTimeout(() => {
          setCopied(false);
        }, 5000);
      },
      onError: () => {
        toastService.error(localization.failed_to_copy_username);
      },
    });
  };

  const handleSendDetails = async () => {
    const response = await authService.sendOneClickSignupDetails({
      countryCode: "+91",
      notificationData,
      isNotificationSendOnEmail,
      isNotificationSendOnPhone,
      userName,
      password,
    });

    if (response.isSuccess) {
      toast.success("Details sent successfully");
    } else {
      toast.error(response.message);
    }

  };

  const clearNotificationDataErrorOnFocus = () => {
    setNotificationDataError((prev) => (prev ? null : prev));
  };

  const handleNotificationDataChange = (value: string) => {
    setNotificationData(value);
    setNotificationDataError((prev) => (value ? null : prev));
  };

  const checkValidation = () => {
    let isValid = true;

    setNotificationDataError(null);

    if (!notificationData) {
      setNotificationDataError("BlankNotificationDataError_message");
      isValid = false;
    }
    else if (verificationChannel === VerificationChannel.Email.value && !Validations.email.regex.test(notificationData)) {
      setNotificationDataError("valid_email");
      isValid = false;
    }
    else if (verificationChannel === VerificationChannel.Phone.value && !Validations.mobile.regex.test(notificationData)) {
      setNotificationDataError("mobile_validation_error");
      isValid = false;
    }
    return isValid;
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (checkValidation()) {
      handleSendDetails();
    }
  };

  return (
    <div>
      <div className="pb-6">
        <div ref={captureRef}>
          <div>
            <TextInput
              label={localization.username}
              type="text"
              placeholder={localization.username}
              value={userName}
              disabled={true}
              faIcon={faCopy}
              iconText={copied ? "Copied" : "Copy"}
              onButtonClick={handleCopy}
            />
          </div>

          <div>
            <PasswordInput
              label={localization.password}
              placeholder={localization.password}
              disabled={true}
              showCopyButton={true}
              showPassword={true}
              value={password}
            />
          </div>
        </div>

        <Button
          type="submit"
          className="mt-2 w-full py-4 text-white bg-primary-gradient rounded-md save-img"
          onClick={handleSaveImage}
        >
          <div className="flex items-center justify-center gap-2">
            <img src={"/icons/ic-save-image.svg"} alt="save-image" />
            <span className="uppercase ">{localization.saveasimage}</span>
          </div>
        </Button>
      </div>

      <div className="border border-[#242426] rounded-lg px-4 py-3 bg-black shadow">
        <Header6 className="font-bold mb-3">
          {localization.send_details_on_email_or_phone}
        </Header6>

        <div className="flex gap-2 mb-3">
          {VerificationChannelsArray.map((channel) => (
            <Button
              key={channel.value}
              className={`px-6 py-2 rounded-md  font-medium ${verificationChannel === channel.value ? "bg-white-gradient text-black" : "border border-[#4B4B4B]"}`}
              onClick={() => {
                setVerificationChannel(channel.value);
                setNotificationData("");
                setIsNotificationSendOnEmail(channel.value === VerificationChannel.Email.value);
                setIsNotificationSendOnPhone(channel.value === VerificationChannel.Phone.value);
              }}
            >
              {localization.getString(channel.value)}
            </Button>
          ))}

        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex gap-3">
            <div className="flex-1">
              {verificationChannel === VerificationChannel.Email.value ? (
                <TextInput
                  value={notificationData}
                  onChange={(e) => handleNotificationDataChange(e.target.value)}
                  placeholder={localization.email}
                  error={notificationDataError && localization.getString(notificationDataError)}
                  onFocus={clearNotificationDataErrorOnFocus}
                  maxLength={240}
                />
              ) : (
                <PhoneNumberInput
                  value={notificationData}
                  maxLength={10}
                  showCountryCode={true}
                  showCountryDropdown={false}
                  onChange={handleNotificationDataChange}
                  placeholder={localization.mobile_number}
                  error={notificationDataError && localization.getString(notificationDataError)}
                  onFocus={clearNotificationDataErrorOnFocus}
                />
              )}
            </div>

            <Button
              type="submit"
              className="h-12 px-6 text-white rounded-md bg-primary-gradient  hover:opacity-90"
            >
              {localization.send}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
